import { useState, useRef, useEffect } from 'react';
import { Box, Text } from '@mantine/core';
import {
  IconPlug, IconSpeakerphone, IconSettings, IconBulb, IconCloudFog,
  IconSnowflake, IconDroplets, IconShieldCheck, IconMusic, IconWindow,
  IconPropeller, IconGasStation, IconKey, IconAlertTriangle, IconCar,
  IconBattery, IconGauge, IconDoor, IconSun, IconMoon, IconArrowBack,
  IconBolt, IconSteeringWheel,
} from '@tabler/icons-react';

// ── Color maps (from StartMyCar JS) ──
const MINI_COLORS: Record<string, string> = {
  '1': '#373737', '2': '#7C9086', '3': '#724998', '4': '#C98F93', '5': '#D88C4E',
  '7.5': '#622f08', '10': '#b70000', '15': '#0124bf', '20': '#C7AF32', '25': '#BCA58F',
  '30': '#1b925d', '35': '#64ad97', '40': '#DA560B', '50': '#900000', '60': '#388db5',
  '70': '#784a1d', '80': '#cbd4b4',
};
const CARTRIDGE_COLORS: Record<string, string> = {
  '15': '#ADAEA8', '20': '#0B80EC', '25': '#EBE8CC', '30': '#F8B7C7', '40': '#0EB159',
  '50': '#C5240E', '60': '#F5D150', '70': '#5A3F1F', '80': '#1D1A18', '100': '#1A3C7F',
};

function getColor(format: string | undefined, amp: string | number | undefined): string {
  if (!amp) return '#333';
  const a = String(amp);
  if ((format || '').includes('cartridge')) return CARTRIDGE_COLORS[a] || MINI_COLORS[a] || '#555';
  return MINI_COLORS[a] || '#555';
}
function isLight(amp: string | number | undefined): boolean {
  return ['20', '25', '60', '80', '120'].includes(String(amp));
}

// ── Icon mapping: relatedEntities → Tabler icons ──
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ENTITY_ICON_MAP: Record<string, any> = {
  ENCENDEDOR: IconPlug, TOMA_12V: IconPlug, TOMA_12V_AUX: IconPlug,
  BOCINA: IconSpeakerphone,
  ECU: IconSettings,
  LUZ_ALTA: IconBulb, LUZ_BAJA: IconBulb, LUZ_ALTA_DER: IconBulb, LUZ_ALTA_IZQ: IconBulb,
  LUZ_BAJA_DER: IconBulb, LUZ_BAJA_IZQ: IconBulb, LUCES_DELANTERAS: IconBulb,
  LUZ_ANTINIEBLA: IconCloudFog,
  AIRE_ACONDICIONADO: IconSnowflake, COMPRESOR_DE_AC: IconSnowflake, CLIMATIZADOR: IconSnowflake,
  LIMPIA_PARABRISAS: IconDroplets, SAPITO: IconDroplets, LAVA_LUNETA: IconDroplets,
  SISTEMA_ABS: IconShieldCheck, BOMBA_ABS: IconShieldCheck, MODULO_ABS: IconShieldCheck,
  AIRBAG: IconAlertTriangle, MODULO_AIRBAG: IconAlertTriangle, TESTIGO_SRS: IconAlertTriangle,
  ESTEREO: IconMusic, AMPLIFICADOR: IconMusic, SUBWOOFER: IconMusic,
  LEVANTAVIDRIOS: IconWindow, VENTANILLA: IconWindow,
  VENTILADOR_RADIADOR: IconPropeller, ELECTROVENTILADOR: IconPropeller, VENTILADOR_DE_CLIMATIZADOR: IconPropeller,
  BOMBA_DE_COMBUSTIBLE: IconGasStation,
  ARRANQUE: IconKey, BURRO_DE_ARRANQUE: IconKey,
  BATERIA: IconBattery, ALTERNADOR: IconBattery,
  TABLERO: IconGauge, PANTALLA: IconGauge, INFOTAINMENT: IconGauge,
  CIERRE_CENTRALIZADO: IconDoor,
  LUZ_DE_HABITACULO: IconSun, LUZ_CORTESIA: IconSun,
  LUCES_TRASERAS: IconMoon, LUZ_POSICION: IconMoon, LUZ_FRENO: IconMoon,
  LUZ_BALIZA: IconAlertTriangle, LUZ_DIRECCIONAL: IconArrowBack,
  LUZ_RETROCESO: IconArrowBack,
  DESEMPANADOR: IconBolt, DESEMPANADOR_TRASERO: IconBolt,
  DIRECCION_ASISTIDA: IconSteeringWheel, DIRECCION_ELECTROASISTIDA: IconSteeringWheel,
  CALEFACCION: IconSnowflake, CALEFACCION_DE_ASIENTO: IconSnowflake,
  CRUISE_CONTROL: IconCar, CONTROL_DE_ESTABILIDAD: IconCar, ESP: IconCar,
  ESPEJO: IconCar, ESPEJO_ELECTRICO: IconCar,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getIcon(entities: string[] | undefined): any {
  if (!entities?.length) return null;
  for (const e of entities) {
    const base = e.replace(/\(.*\)$/, '');
    if (ENTITY_ICON_MAP[base]) return ENTITY_ICON_MAP[base];
  }
  return null;
}

function formatType(kind: FuseItem['kind']): string {
  if (!kind) return '';
  const t = kind.type || '';
  const f = kind.format || '';
  if (t === 'relay') return 'Relay';
  if (t === 'fuse') {
    if (f.includes('cartridge')) return 'Fuse FMX/JCase';
    if (f === 'maxi') return 'Fuse MAXI';
    return 'Fuse MINI';
  }
  return t;
}

// ── Types ──
interface FuseItem {
  id: string;
  kind?: { type?: string; format?: string; amp?: string | number; notUsed?: boolean };
  layout?: { top: number; left: number; width: number; height: number };
  description?: string;
  relatedEntities?: string[];
}

interface FuseBoxData {
  fuses: FuseItem[];
  boxName?: string;
  boxAspectRatio?: number;
  boxDiagramImg?: { url: string } | string;
  boxThumbnail?: string;
}

interface Props {
  boxes: FuseBoxData[];
  highlightFuseId?: string;
}

// ── Main Component ──
export function FuseBoxDiagram({ boxes, highlightFuseId }: Props) {
  const [activeBox, setActiveBox] = useState(0);
  const [selectedFuse, setSelectedFuse] = useState<FuseItem | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  if (!boxes?.length) return null;
  const box = boxes[activeBox] || boxes[0];
  const allFuses = box.fuses || [];
  const layoutFuses = allFuses.filter(f => f.layout && f.layout.width > 0);
  const tableFuses = allFuses.filter(f => f.description && f.kind?.amp);
  const bgUrl = typeof box.boxDiagramImg === 'string' ? box.boxDiagramImg : box.boxDiagramImg?.url;
  const hasVisibleFuses = layoutFuses.some(f => f.kind?.amp || (f.kind?.type === 'relay'));

  // Auto-highlight fuse from LLM answer on mount
  useEffect(() => {
    if (highlightFuseId) {
      const fuse = allFuses.find(f => f.id === highlightFuseId);
      if (fuse) setSelectedFuse(fuse);
    }
  }, [highlightFuseId, activeBox]);

  const scrollToRow = (id: string) => {
    const el = tableRef.current?.querySelector(`[data-fuse-id="${id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // from diagram: select + scroll table row into view
  const selectFromDiagram = (fuse: FuseItem | null) => {
    setSelectedFuse(prev => prev?.id === fuse?.id ? null : fuse);
    // don't scroll — stay on diagram
  };

  // from table: select + scroll detail into view (top)
  const selectFromTable = (fuse: FuseItem | null) => {
    setSelectedFuse(prev => prev?.id === fuse?.id ? null : fuse);
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Sticky: Tabs + Selected Detail ── */}
      <Box style={{ position: 'sticky', top: 0, zIndex: 10, background: '#0C0C0E', paddingBottom: 6 }}>
        {boxes.length > 1 && (
          <Box style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {boxes.map((b, i) => (
              <Box key={i} onClick={() => { setActiveBox(i); setSelectedFuse(null); }}
                style={{
                  padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: i === activeBox ? '#2563EB' : '#27272A',
                  color: i === activeBox ? '#fff' : '#A1A1AA',
                }}>
                {(() => {
                  const name = b.boxName || (i === 0 ? 'Interior' : `Panel ${i + 1}`);
                  // Deduplicate: if same name appears before, append number
                  const dupeCount = boxes.slice(0, i).filter(bb => (bb.boxName || '') === (b.boxName || '')).length;
                  return dupeCount > 0 ? `${name} (${dupeCount + 1})` : name;
                })()}
              </Box>
            ))}
          </Box>
        )}

        {selectedFuse && (
          <Box px={14} py={10} style={{ background: '#18181B', borderRadius: 8, border: '1px solid #2563EB', marginBottom: 6 }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {(() => { const Icon = getIcon(selectedFuse.relatedEntities); return Icon ? <Icon size={28} color="#3B82F6" /> : null; })()}
              <Box>
                <Text size="md" fw={700} c="#E4E4E7">
                  {selectedFuse.id} <Text span c="#71717A" fw={400}>{formatType(selectedFuse.kind)}</Text>
                  {selectedFuse.kind?.amp && <Text span c="#3B82F6"> {selectedFuse.kind.amp}A</Text>}
                </Text>
                <Text size="sm" c="#A1A1AA">{selectedFuse.description || 'No circuit assigned'}</Text>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Diagram photo (only show if boxThumbnail is explicitly provided — generic bgUrl often mismatched) ── */}
      {box.boxThumbnail && (
        <Box style={{ marginBottom: 12, borderRadius: 8, overflow: 'hidden', border: '1px solid #27272A' }}>
          <img src={encodeURI(box.boxThumbnail)} alt={box.boxName || 'Fuse box'} style={{ width: '100%', display: 'block', borderRadius: 8 }} />
        </Box>
      )}

      {/* ── Table: Type | No. | Description ── */}
      <Box ref={tableRef}>
        <Box style={{ display: 'flex', padding: '10px 12px', borderBottom: '1px solid #333' }}>
          <Text size="md" fw={700} c="#71717A" style={{ width: 110 }}>Type</Text>
          <Text size="md" fw={700} c="#71717A" style={{ width: 50 }}>No.</Text>
          <Text size="md" fw={700} c="#71717A" style={{ flex: 1 }}>Description</Text>
        </Box>
        {tableFuses.map((fuse) => {
          const amp = fuse.kind?.amp;
          const color = getColor(fuse.kind?.format, amp);
          const isSel = selectedFuse?.id === fuse.id;
          const isHi = highlightFuseId === fuse.id;
          const Icon = getIcon(fuse.relatedEntities);

          return (
            <Box key={fuse.id} data-fuse-id={fuse.id}
              onClick={() => selectFromTable(fuse)}
              style={{
                display: 'flex', alignItems: 'center', padding: '10px 12px', cursor: 'pointer',
                background: isSel ? '#1e293b' : isHi ? 'rgba(59,130,246,0.1)' : 'transparent',
                borderLeft: isHi ? '3px solid #3B82F6' : '3px solid transparent',
                borderBottom: '1px solid #1a1a1a',
              }}
            >
              {/* Type + color chip */}
              <Box style={{ width: 110, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Box style={{ width: 38, height: 24, borderRadius: 4, background: color, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text size="sm" fw={700} c={isLight(amp) ? '#000' : '#fff'}>{amp}</Text>
                </Box>
                <Text size="sm" c="#71717A">{formatType(fuse.kind)}</Text>
              </Box>
              {/* Number */}
              <Text size="md" fw={700} c="#A1A1AA" style={{ width: 50 }}>{fuse.id}</Text>
              {/* Icon + Description */}
              <Box style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                {Icon && <Icon size={18} color="#666" />}
                <Text size="md" c="#E4E4E7" lineClamp={1}>{fuse.description}</Text>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
